import React, { useState } from "react";
import { X, File, Calendar, Database, Layers, Clock } from "lucide-react";

interface CompareDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: any;
}

const CompareDetailsModal: React.FC<CompareDetailsModalProps> = ({
  isOpen,
  onClose,
  data,
}) => {
  if (!isOpen || !data) return null;

  const sourceFile = data.source_file_data;
  const targetFile = data.target_file_data;

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatFileSize = (bytes: number) => {
    if (!bytes) return "N/A";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + " KB";
    return (bytes / (1024 * 1024)).toFixed(2) + " MB";
  };

  const FileInfoCard = ({ file, title }: any) => {
    const [expanded, setExpanded] = useState(false);

    if (!file) {
      return (
        <div className="border rounded-lg p-4 text-center text-slate-500 bg-slate-50">
          No file data available
        </div>
      );
    }

    return (
      <div className="space-y-5">
        <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
          <File className="h-5 w-5 text-slate-600" />
          {title}
        </h3>

        <div className="space-y-3">
          {/* File Name */}
          <div className="border rounded-lg p-3">
            <div className="text-xs font-medium text-slate-600 mb-1">
              File Name
            </div>
            <div className="text-sm font-semibold text-slate-900 break-all">
              {file.name}
            </div>
          </div>

          {/* File Details Grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="border rounded-lg p-3">
              <div className="text-xs text-slate-600 mb-1 flex items-center gap-1">
                <Database className="h-3 w-3" />
                Size
              </div>
              <div className="text-sm font-semibold text-slate-900">
                {formatFileSize(file.file_size)}
              </div>
            </div>

            <div className="border rounded-lg p-3">
              <div className="text-xs text-slate-600 mb-1 flex items-center gap-1">
                <Layers className="h-3 w-3" />
                Pages
              </div>
              <div className="text-sm font-semibold text-slate-900">
                {file.file_pages || "N/A"}
              </div>
            </div>

            <div className="border rounded-lg p-3">
              <div className="text-xs text-slate-600 mb-1">Type</div>
              <div className="text-sm font-semibold text-slate-900">
                {file.file_type || "N/A"}
              </div>
            </div>

            <div className="border rounded-lg p-3">
              <div className="text-xs text-slate-600 mb-1">Processing Type</div>
              <div className="text-sm font-semibold text-slate-900">
                {file.file_processing_type || "N/A"}
              </div>
            </div>
          </div>

          {/* Uploaded Info */}
          <div className="border rounded-lg p-3">
            <div className="text-xs text-slate-600 mb-1 flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              Uploaded At
            </div>
            <div className="text-sm font-semibold text-slate-900">
              {formatDate(file.uploaded_at)}
            </div>
          </div>

          {/* Status */}
          <div className="border rounded-lg p-3">
            <div className="text-xs text-slate-600 mb-1">Status</div>
            <span
              className={`px-2 py-1 text-xs font-semibold rounded ${
                file.overall_processed_status === "DONE"
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-amber-100 text-amber-700"
              }`}
            >
              {file.overall_processed_status || "N/A"}
            </span>
          </div>

          {/* AI Level */}
          {file.ai_performance_level && (
            <div className="border rounded-lg p-3">
              <div className="text-xs text-slate-600 mb-1">
                AI Performance Level
              </div>
              <div className="text-sm font-semibold text-slate-900">
                {file.ai_performance_level}
              </div>
            </div>
          )}

          {/* Processing Time */}
          {file.overall_processing && (
            <div className="border rounded-lg p-3">
              <div className="text-xs text-slate-600 mb-1 flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Processing Time
              </div>
              <div className="text-sm font-semibold text-slate-900">
                {file.overall_processing.time_taken
                  ? `${file.overall_processing.time_taken.toFixed(2)}s`
                  : "N/A"}
              </div>
            </div>
          )}

          {/* File Summary */}
          {file.file_summary && (
            <div className="border rounded-lg p-3 bg-slate-50">
              <div className="text-xs text-slate-600 mb-2">File Summary</div>
              <div
                className={`text-sm text-slate-700 leading-relaxed ${
                  expanded ? "max-h-[400px]" : "max-h-28"
                } overflow-y-auto whitespace-pre-wrap`}
              >
                {file.file_summary}
              </div>
              <button
                onClick={() => setExpanded(!expanded)}
                className="mt-2 text-xs font-medium text-indigo-600 hover:text-indigo-700"
              >
                {expanded ? "Show Less" : "Show More"}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-slate-50">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              Compare Details
            </h2>
            <p className="text-xs text-slate-500">
              Source and Target file comparison summary
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-200 transition-colors"
          >
            <X className="h-5 w-5 text-slate-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Top Info */}
          <div className="mb-5 border rounded-lg p-4 bg-slate-50">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-xs text-slate-600 mb-1">Compare ID</div>
                <div className="font-mono text-slate-800 break-all">
                  {data._id}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-600 mb-1">Status</div>
                <span
                  className={`px-2 py-1 text-xs font-semibold rounded ${
                    data.document_compare_status === "DONE"
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-amber-100 text-amber-700"
                  }`}
                >
                  {data.document_compare_status}
                </span>
              </div>
              <div>
                <div className="text-xs text-slate-600 mb-1">Started</div>
                <div className="font-semibold text-slate-800">
                  {formatDate(data.document_compare_start_time)}
                </div>
              </div>
            </div>

            {/* Summary */}
            {data.result?.comparision_result && (
              <div className="mt-4 grid grid-cols-4 gap-3">
                <div className="border rounded-lg p-2 text-center">
                  <div className="text-xs text-slate-600">Similarity</div>
                  <div className="text-base font-bold text-slate-900">
                    {data.result.comparision_result.Similarity}
                  </div>
                </div>
                <div className="border rounded-lg p-2 text-center">
                  <div className="text-xs text-slate-600">Total Changes</div>
                  <div className="text-base font-bold text-slate-900">
                    {data.result.comparision_result.total_changes}
                  </div>
                </div>
                <div className="border rounded-lg p-2 text-center">
                  <div className="text-xs text-slate-600">Words Compared</div>
                  <div className="text-base font-bold text-slate-900">
                    {data.result.comparision_result.words_compared}
                  </div>
                </div>
                <div className="border rounded-lg p-2 text-center">
                  <div className="text-xs text-slate-600">Differences</div>
                  <div className="text-base font-bold text-slate-900">
                    {data.result.differences?.length || 0}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* File Info */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <FileInfoCard file={sourceFile} title="Source File" />
            <FileInfoCard file={targetFile} title="Target File" />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-slate-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-900 text-sm font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompareDetailsModal;
